import { FileText, Shield, Stethoscope } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatField } from "@/services/coverageMappers";
import { EmptyState, FieldRow, SectionCard, SourceChip, statusBadge } from "@/components/coverage/coverageUi";

function formatRequired(value) {
  if (value == null) return "Not provided";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    if (value.value === true) return "Yes";
    if (value.value === false) return "No";
    if (value.status === "NOT_PROVIDED_BY_PAYER") return "Not provided";
  }
  return String(value);
}

export default function ClinicalPolicyPanel({ workspace }) {
  const dental = workspace.dental_policy || {};
  const lookup = dental.lookup || {};

  return (
    <div className="space-y-4" data-testid="coverage-clinical-panel">
      <SectionCard
        title="Category coinsurance"
        icon={<Stethoscope className="w-4 h-4 text-emerald-400" />}
      >
        <p className="text-xs text-slate-500 -mt-1">
          Plan coverage % from Stedi service benefits; member coinsurance from financial benefits when present.
        </p>
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="text-slate-400">Category</TableHead>
              <TableHead className="text-slate-400">Plan IN</TableHead>
              <TableHead className="text-slate-400">Plan OON</TableHead>
              <TableHead className="text-slate-400">Member IN</TableHead>
              <TableHead className="text-slate-400">Member OON</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(workspace.benefit_matrix || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-slate-500">
                  No service benefits on this payload.
                </TableCell>
              </TableRow>
            )}
            {(workspace.benefit_matrix || []).map((row) => (
              <TableRow
                key={row.label}
                className={`border-slate-800 ${row.is_ortho ? "bg-emerald-500/5" : ""}`}
              >
                <TableCell className="text-slate-200">
                  {row.label}
                  {row.is_ortho ? (
                    <span className="ml-2 text-[10px] uppercase text-emerald-400">ortho</span>
                  ) : null}
                </TableCell>
                <TableCell className="text-emerald-400 font-mono">{row.in_network}</TableCell>
                <TableCell className="text-slate-300 font-mono">{row.out_of_network}</TableCell>
                <TableCell className="text-slate-300 font-mono">{row.member_in}</TableCell>
                <TableCell className="text-slate-300 font-mono">{row.member_out}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      <SectionCard
        title="Lakehouse dental policy"
        icon={<FileText className="w-4 h-4 text-emerald-400" />}
        badge={<SourceChip>{dental.status || "payer knowledge"}</SourceChip>}
      >
        {!dental.present ? (
          <EmptyState
            title="No Databricks lakehouse dental policy on this record"
            detail={
              dental.status
                ? `Payer knowledge status: ${dental.status}`
                : "code_rules, documentation, and plan lookup will render here when Coverage API enrichment is present."
            }
          />
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-2">
              <FieldRow label="Payer" value={lookup.payer} />
              <FieldRow label="Plan" value={lookup.plan} />
              <FieldRow label="State" value={lookup.state} />
              <FieldRow label="Product / LOB" value={[lookup.product, lookup.lob].filter(Boolean).join(" · ")} />
              <FieldRow label="Coverage ID" value={lookup.coverage_id} />
              <FieldRow label="Codes" value={(lookup.codes || []).join(", ")} />
            </div>

            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold pt-2">
              Procedure-relevant limits
            </p>
            {(workspace.procedure_rules || []).length === 0 && (workspace.code_rules || []).length === 0 ? (
              <EmptyState title="No CDT code rules on payer knowledge" />
            ) : (
              (workspace.procedure_rules?.length ? workspace.procedure_rules : workspace.code_rules).map((rule) => (
                <div key={rule.code || rule.description} className="rounded-lg border border-slate-800 p-3 text-sm">
                  <p className="text-white font-medium">
                    {rule.code} · {rule.description || rule.service}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-1 mt-2 text-slate-300">
                    <FieldRow
                      label="Member cost"
                      value={
                        formatField(rule.member_cost)
                          ? `${rule.cost_type ? `${rule.cost_type} · ` : ""}${formatField(rule.member_cost)}`
                          : "Not provided"
                      }
                    />
                    <FieldRow label="Frequency" value={formatField(rule.frequency_limit) || "Not provided"} />
                    <FieldRow label="Age limit" value={formatField(rule.age_limit) || "Not provided"} />
                    <FieldRow label="Applies to" value={rule.applies_to || "Not provided"} />
                  </div>
                  {rule.page_start ? (
                    <p className="text-xs text-slate-500 mt-2">
                      Source pp. {rule.page_start}
                      {rule.page_end ? `–${rule.page_end}` : ""} · {rule.source_id || ""}
                    </p>
                  ) : null}
                </div>
              ))
            )}

            {(workspace.service_assessments || []).map((svc) => (
              <div key={svc.code} className="border border-slate-800 rounded-lg p-3">
                <p className="text-white font-medium">
                  {svc.code} · {svc.service} · {svc.status}
                </p>
                {(svc.notes || []).map((n, i) => (
                  <p key={i} className="text-xs text-slate-500 mt-1">
                    {n}
                  </p>
                ))}
              </div>
            ))}
          </>
        )}
      </SectionCard>

      <SectionCard title="Prior auth & documentation" icon={<Shield className="w-4 h-4 text-emerald-400" />}>
        <FieldRow
          label="PA required"
          value={
            workspace.prior_authorization
              ? String(workspace.prior_authorization.required)
              : "Not provided"
          }
        />
        {workspace.prior_authorization?.summary ? (
          <p className="text-slate-400 text-sm">{workspace.prior_authorization.summary}</p>
        ) : null}
        {(dental.prior_auth_policies || []).map((policy) => (
          <div key={policy.policy_id || policy.summary} className="rounded-lg border border-slate-800 p-3 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white font-medium capitalize">{(policy.review_type || "policy").replace(/_/g, " ")}</p>
              {statusBadge(formatRequired(policy.is_required))}
            </div>
            {policy.summary ? <p className="text-slate-300 mt-1">{policy.summary}</p> : null}
            <p className="text-xs text-slate-500 mt-1">
              Applies to {policy.applies_to || "—"}
              {policy.threshold_amount?.value != null ? ` · threshold $${policy.threshold_amount.value}` : ""}
            </p>
          </div>
        ))}
        {!dental.documentation_requirements?.length ? (
          <EmptyState title="No documentation requirements on lakehouse policy" />
        ) : (
          dental.documentation_requirements.map((doc, idx) => (
            <p key={idx} className="text-sm text-slate-300">
              {doc.text}
              {doc.applies_to ? <span className="text-slate-500"> · {doc.applies_to}</span> : null}
            </p>
          ))
        )}
      </SectionCard>
    </div>
  );
}
